import moment = require("moment");
import React = require("react");
import {JSONPatch, Operation, RestDataLoader} from "../../../dashboard/widgets/unilever/RestDataLoader";
import "./EquipmentStockLevel.scss";
import {SettingSection} from "./SettingSection";
import {UsageBar} from "./UsageBar";
import {ThemeProvider} from "@vebox/etui-system";
import {veboxDefaultTheme} from "../../../index";
import {lerpColor} from "../../../dashboard/operativity/charts/common";

enum EquipmentStockLevelStatus {
    /** The tab is fetching the configuration from the server. */
    Initializing,
    /** The tab is ready to display the data. */
    Ready,
    /** The stock level is missing on the equipment */
    Missing,
}

interface Stock {
    /** The equipment capacity in number of items */
    capacity: number;
    /** The remaining items percentage before the stock is empty */
    stockPercent: number;
    /** The remaining stock units respect to the capacity */
    stock: number;
    /** Last stock update */
    date: Date;
    /** Last stock update (local date respect to the component's location */
    localDate: Date;
}

interface EquipmentStockLevelTexts {
    title: string;
    inEditingText: string;
    configuredTitleValue: string;
    notConfiguredTitleValue: string;
    editButtonText: string;
    saveButtonText: string;
    cancelButtonText: string;
    deleteConfButtonText: string;
    selectTheCurrentStock: string;
    selectTheNewStockLevel: string;
    stockUnitOfMeasure: string;
    totalCapacity: string;
}

interface EquipmentStockLevelConfiguration {
    equipmentGuid: string;
    allowEditing: boolean;
    texts: EquipmentStockLevelTexts;
}

interface EquipmentStockLevelProps {
    equipmentId: number;
    onOpen: () => any;
    closeOthers: () => any;
    onEdit?: () => void;
}

interface EquipmentStockLevelState {
    status: EquipmentStockLevelStatus;
    configuration: EquipmentStockLevelConfiguration;
    currentStock: Stock;
    selectedCurrentStock: number;
    selectedStockFeedback: number;
    closed: boolean;
}

export class EquipmentStockLevel extends React.Component<EquipmentStockLevelProps, EquipmentStockLevelState> {
    private static logEnabled: boolean = false;

    constructor(props) {
        super(props);
        this.toggle = this.toggle.bind(this);
        this.save = this.save.bind(this);
        this.close = this.close.bind(this);
        this.setSelectedCurrentStock = this.setSelectedCurrentStock.bind(this);
        this.state = {
            status: EquipmentStockLevelStatus.Initializing,
            configuration: null,
            currentStock: null,
            selectedCurrentStock: null,
            selectedStockFeedback: null,
            closed: true,
        };
    }

    public componentDidMount(): void {
        this.retrieveConfiguration();
    }

    public render() {
        if (this.state.status === EquipmentStockLevelStatus.Initializing
            || this.state.status === EquipmentStockLevelStatus.Missing
            || this.state.currentStock === null) {
            return null;
        }
        const content = (
            <>
                <EquipmentStockEdit
                    title={this.state.configuration.texts.selectTheCurrentStock}
                    currentStock={this.state.selectedStockFeedback}
                    capacity={this.state.currentStock.capacity}
                    unitOfMeasure={this.state.configuration.texts.stockUnitOfMeasure}
                    onStockChange={this.setSelectedStockFeedback}
                />
                <EquipmentStockEdit
                    title={this.state.configuration.texts.selectTheNewStockLevel}
                    currentStock={this.state.selectedCurrentStock}
                    capacity={this.state.currentStock.capacity}
                    unitOfMeasure={this.state.configuration.texts.stockUnitOfMeasure}
                    onStockChange={this.setSelectedCurrentStock}
                />
            </>
        );
        return (
            <ThemeProvider theme={veboxDefaultTheme}>
                <React.Fragment>
                    <SettingSection
                        title={this.state.configuration.texts.title}
                        subtitle={moment(new Date(this.state.currentStock.localDate)).format("DD/MM/YYYY HH:mm")}
                        content={
                            <div className={"stock-level-bar"}>
                                <UsageBar
                                    capacity={this.state.currentStock.capacity}
                                    unitOfMeasure={this.state.configuration.texts.stockUnitOfMeasure}
                                    currentValue={this.state.currentStock.stock}
                                    currentValuePercentage={Math.round(this.state.currentStock.stockPercent * 100)}
                                />
                                <div style={{marginTop: "20px", fontSize: "14px"}}>
                                    <p>{this.state.configuration.texts.totalCapacity}: <span
                                        style={{
                                            fontStyle: "italic",
                                            fontWeight: "bold",
                                        }}
                                    >
                                    {/* tslint:disable-next-line:max-line-length */}
                                        {this.state.currentStock.capacity} {this.state.configuration.texts.stockUnitOfMeasure}
                                </span>
                                    </p>
                                </div>
                            </div>
                        }
                        editingContent={content}
                        onToggle={this.toggle}
                        onSave={this.save}
                        onCancel={this.close}
                        onDeleteConf={null}
                        configuration={this.state.configuration.texts}
                        contentTitle={Math.round(this.state.currentStock.stockPercent * 100) + "%"}
                        isInEditing={!this.state.closed}
                        showSaveButton={
                            this.state.selectedCurrentStock !== null
                            && this.state.selectedStockFeedback !== null
                        }
                        allowEditing={this.state.configuration.allowEditing}
                    />
                </React.Fragment>
            </ThemeProvider>
        );
    }

    public close() {
        if (this.state.closed) {
            return;
        }
        this.setState(
            {
                closed: true,
                selectedCurrentStock: null,
                selectedStockFeedback: null,
            },
        );
    }

    private save() {
        if (this.state.selectedCurrentStock === null && this.state.selectedStockFeedback === null) {
            return;
        }
        const patch = new JSONPatch();
        if (this.state.selectedCurrentStock !== null) {
            patch.add(
                new Operation(
                    "/stockPercent",
                    this.state.selectedCurrentStock / 100,
                ),
            );
        }
        if (this.state.selectedStockFeedback !== null) {
            patch.add(
                new Operation(
                    "/stockFeedbackPercent",
                    this.state.selectedStockFeedback / 100,
                ),
            );
        }
        RestDataLoader.patchJSON(
            "equipment/" + this.state.configuration.equipmentGuid + "/stockLevel",
            patch,
            (data) => {
                this.setState({currentStock: data}, () => {
                    this.close();
                    if (this.props.onEdit) {
                        this.props.onEdit();
                    }
                });
            },
            () => {
                console.error("Error performing patch");
            },
        );
    }

    private setSelectedCurrentStock(value: number) {
        this.setState({selectedCurrentStock: value});
    }

    private setSelectedStockFeedback = (value: number) => {
        this.setState({selectedStockFeedback: value});
    }

    private toggle(): void {
        if (this.state.closed) {
            this.setState({closed: false});
            this.props.closeOthers();
            this.props.onOpen();
        } else {
            this.close();
        }
    }

    private retrieveConfiguration(): void {
        this.log("Retrieving configuration");
        const url = "equipment/" + this.props.equipmentId + "/stock-level-configuration";
        Ajax.ajaxRequestForJson(
            {
                url,
                type: "GET",
                success: (response: EquipmentStockLevelConfiguration) => {
                    this.log("Success retrieving configuration");
                    this.log(response);
                    this.setState({configuration: response});
                    this.retrieveStock();
                },
                error: () => {
                    console.error("Error requesting the equipment stock configuration");
                    this.setState({status: EquipmentStockLevelStatus.Missing});
                },
            },
        );
    }

    private retrieveStock() {
        RestDataLoader.loadData(
            "equipment/" + this.state.configuration.equipmentGuid + "/stockLevel",
            null,
            (result) => {
                this.setState({currentStock: result, status: EquipmentStockLevelStatus.Ready});
            },
            () => {
                this.setState({status: EquipmentStockLevelStatus.Missing});
            },
        );
    }

    // noinspection JSMethodCanBeStatic
    private log(message: any): void {
        if (EquipmentStockLevel.logEnabled) {
            console.log(message);
        }
    }
}

interface EquipmentStockEditProps {
    title: string;
    currentStock: number;
    capacity: number;
    unitOfMeasure: string;
    onStockChange: (value: number) => void;
}

const stockValues = [0, 25, 50, 75, 100];

const colors = ["#FC594D", "#FFBC42", "#dede6dfc", "#8ce7c7", "#00C399"];
const EquipmentStockEdit: React.FunctionComponent<EquipmentStockEditProps> = (props) => {

    const toQuantity = (percentage: number) => {
        return Math.round(props.capacity * percentage / 100);
    };
    const content = stockValues.map((value, index) => {
        const onClick = () => {
            props.onStockChange(value);
        };
        let classElement = "stock-level-elements__element";
        if (props.currentStock === value) {
            classElement = "stock-level-elements__element stock-level-elements__element--selected";
        }

        return (
            <div
                key={index}
                onClick={onClick}
                className={classElement}
                style={{
                    backgroundColor: props.currentStock !== value ? colors[index] : undefined,
                }}
            >
                <p className={"stock-value"}>{value}%</p>
                <p className={"lower-italic"}>
                    {toQuantity(value)} {props.unitOfMeasure}
                </p>
            </div>
        );
    });
    return (
        <div className={"stock-level-container"}>
            <p className={"stock-level-title"}>{props.title}</p>
            <div className={"stock-level-elements"}>
                {content}
            </div>
        </div>
    );
};
